import { MINUTE } from '@constants/various';
import QueryKeys from '@constants/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteWorkerAvailability,
  getWorkerAvailability,
} from '@worker/services/MyJobs/api';
import { getWorker, getWorkerOptions } from '@worker/services/worker/api';
import useNotification from '@hooks/useNotification';

export const useWorkerProfile = () => {
  return useQuery({
    queryKey: [QueryKeys.WORKER_PROFILE],
    queryFn: getWorker,
    staleTime: MINUTE * 5, // Consider data fresh for 5 minutes
    refetchOnMount: false, // Don't refetch on component mount if data exists
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};

export const useWorkerOptions = () => {
  return useQuery({
    queryKey: [QueryKeys.WORKER_OPTIONS],
    queryFn: getWorkerOptions,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useWorkerAvailability = () => {
  return useQuery({
    queryKey: ['worker-availabilities'],
    queryFn: getWorkerAvailability,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * Hook to delete worker availability
 * Invalidates query on success to refetch latest data (consistent with add operation)
 */
export const useDeleteWorkerAvailability = () => {
  const queryClient = useQueryClient();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  return useMutation({
    mutationFn: deleteWorkerAvailability,
    onSuccess: () => {
      // Invalidate and refetch - same approach as add operation
      queryClient.invalidateQueries({ queryKey: ['worker-availabilities'] });

      // Show success notification
      showSuccessNotification('Availability deleted successfully!');
    },
    onError: (error) => {
      console.error('Failed to delete availability:', error);
      showErrorNotification('Failed to delete availability. Please try again.');
    },
  });
};
